/**
 * Iter 704 — whole-website language switch.
 *
 * `t("English text", vars?)` renders the active language's translation of
 * the EXACT English string (keys ARE the English copy — no separate key
 * file). The provider:
 *  • rides the existing Iter-276f language pill (useLangPref broadcast);
 *  • translates via POST /api/i18n/bundle in ≤400-string chunks, cached
 *    per language in localStorage (repeat loads are instant, zero flash);
 *  • self-heals: any t() string not yet translated is queued and fetched
 *    in a debounced follow-up — no manual catalog to maintain;
 *  • loads category names from GET /api/i18n/categories?lang= and exposes
 *    tCat(englishName) for nav/category surfaces;
 *  • sets <html lang> + a `lang` cookie; placeholders like {name} survive
 *    translation and are interpolated after lookup.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { sharedApi } from "@/lib/api";
import useLangPref from "@/hooks/useLangPref";

const I18nContext = createContext({ lang: "en", t: (s) => s, tCat: (s) => s, ready: true });

const CACHE_KEY = (lang) => `i18n:${lang}`;
const CHUNK = 400;

// Module-level registry of every English string the UI has asked for —
// survives provider re-renders, feeds the bundle requests.
const REGISTRY = new Set();

const interpolate = (text, vars) =>
  vars ? text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m)) : text;

function readCache(lang) {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY(lang)) || "{}") || {};
  } catch {
    return {};
  }
}
function writeCache(lang, dict) {
  try {
    localStorage.setItem(CACHE_KEY(lang), JSON.stringify(dict));
  } catch {
    /* quota — translations still live in memory */
  }
}

export function I18nProvider({ children }) {
  const [lang] = useLangPref();
  const [dict, setDict] = useState(() => (lang === "en" ? {} : readCache(lang)));
  const [catMap, setCatMap] = useState({});
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);
  const langRef = useRef(lang);
  langRef.current = lang;

  // <html lang> + cookie so crawlers/headers can read the choice.
  useEffect(() => {
    document.documentElement.lang = lang || "en";
    try {
      document.cookie = `lang=${lang || "en"};path=/;max-age=31536000;SameSite=Lax`;
    } catch {
      /* noop */
    }
  }, [lang]);

  const fetchStrings = useCallback(async (strings, forLang) => {
    const todo = [...strings];
    for (let i = 0; i < todo.length; i += CHUNK) {
      const chunk = todo.slice(i, i + CHUNK);
      const payload = {};
      chunk.forEach((s) => {
        payload[s] = s;
      });
      try {
        const { data } = await sharedApi.post("/i18n/bundle", { lang: forLang, strings: payload });
        if (langRef.current !== forLang) return; // user switched again
        setDict((prev) => {
          const next = { ...prev, ...(data?.strings || {}) };
          writeCache(forLang, next);
          return next;
        });
      } catch {
        return; // keep English for this chunk; retried on next switch
      }
    }
  }, []);

  // Language switch: hydrate from cache, then fetch whatever the registry
  // has that the cache doesn't.
  useEffect(() => {
    if (lang === "en") {
      setDict({});
      setCatMap({});
      return;
    }
    const cached = readCache(lang);
    setDict(cached);
    const missing = [...REGISTRY].filter((s) => !(s in cached));
    if (missing.length) fetchStrings(missing, lang);
    // Category names (339 rows, cached server-side).
    sharedApi
      .get("/i18n/categories", { params: { lang } })
      .then(({ data }) => {
        if (langRef.current !== lang) return;
        const m = {};
        (data?.categories || data || []).forEach((c) => {
          if (c?.name_en && c?.name) m[String(c.name_en).toLowerCase()] = c.name;
        });
        setCatMap(m);
      })
      .catch(() => setCatMap({}));
  }, [lang, fetchStrings]);

  // Debounced self-heal for strings first seen after the switch.
  const queueMissing = useCallback(
    (s) => {
      if (langRef.current === "en") return;
      pendingRef.current.add(s);
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const batch = [...pendingRef.current];
        pendingRef.current.clear();
        if (batch.length) fetchStrings(batch, langRef.current);
      }, 900);
    },
    [fetchStrings],
  );

  const t = useCallback(
    (text, vars) => {
      if (typeof text !== "string" || !text) return text;
      REGISTRY.add(text);
      if (lang === "en") return interpolate(text, vars);
      const hit = dict[text];
      if (hit == null) {
        queueMissing(text);
        return interpolate(text, vars);
      }
      return interpolate(hit, vars);
    },
    [lang, dict, queueMissing],
  );

  const tCat = useCallback(
    (nameEn) => {
      if (lang === "en" || typeof nameEn !== "string" || !nameEn) return nameEn;
      return catMap[nameEn.toLowerCase()] || nameEn;
    },
    [lang, catMap],
  );

  const value = useMemo(() => ({ lang, t, tCat, ready: true }), [lang, t, tCat]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
